import React from 'react';
import { Card, CardBody, CardHeader, Divider } from "@heroui/react";
import { Shield, Eye, Lock, UserCheck, Mail, Phone } from "lucide-react";

function Privacy() {
  return (
    <div className="container mx-auto  py-8 w-full px-10 mt-14">
      {/* Header Section */}
      <div className="text-center mb-8">
        <div className="flex justify-center mb-4">
          <Shield className="w-16 h-16 text-blue-600" />
        </div>
        <h1 className="text-4xl font-bold text-gray-800 mb-4">Privacy Policy</h1>
        <p className="text-lg text-gray-600">
          Your privacy is important to us. This policy explains how we collect, use, and protect your information.
        </p>
        {/* <p className="text-sm text-gray-500 mt-2">
          Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </p> */}
      </div>

      <div className="space-y-6">
        {/* Information We Collect */}
        <Card className="shadow-lg">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <Eye className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-semibold text-gray-800">Information We Collect</h2>
            </div>
          </CardHeader>
          <Divider />
          <CardBody className="pt-4">
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Personal Information</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-1">
                  <li>Name and contact information (email, phone number)</li>
                  <li>Educational background and academic details</li>
                  <li>Profile information and preferences</li>
                  <li>Payment and billing information</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Usage Information</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-1">
                  <li>Course progress and performance data</li>
                  <li>Website usage patterns and analytics</li>
                  <li>Device information and IP addresses</li>
                  <li>Cookies and similar tracking technologies</li>
                </ul>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* How We Use Your Information */}
        <Card className="shadow-lg">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <UserCheck className="w-6 h-6 text-green-600" />
              <h2 className="text-2xl font-semibold text-gray-800">How We Use Your Information</h2>
            </div>
          </CardHeader>
          <Divider />
          <CardBody className="pt-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Service Provision</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-1">
                  <li>Provide access to courses and content</li>
                  <li>Track learning progress</li>
                  <li>Process payments and transactions</li>
                  <li>Send course-related communications</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Improvement & Support</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-1">
                  <li>Improve our platform and services</li>
                  <li>Provide customer support</li>
                  <li>Analyze usage patterns</li>
                  <li>Prevent fraud and ensure security</li>
                </ul>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Data Protection */}
        <Card className="shadow-lg">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <Lock className="w-6 h-6 text-red-600" />
              <h2 className="text-2xl font-semibold text-gray-800">Data Protection & Security</h2>
            </div>
          </CardHeader>
          <Divider />
          <CardBody className="pt-4">
            <div className="space-y-4">
              <p className="text-gray-600">
                We implement appropriate technical and organizational measures to protect your personal data against 
                unauthorized access, alteration, disclosure, or destruction.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-blue-800 mb-2">Technical Safeguards</h3>
                  <ul className="text-blue-700 text-sm space-y-1">
                    <li>• Encryption of sensitive data</li>
                    <li>• Secure server infrastructure</li>
                    <li>• Regular security updates</li>
                    <li>• Access controls and monitoring</li>
                  </ul>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-green-800 mb-2">Organizational Measures</h3>
                  <ul className="text-green-700 text-sm space-y-1">
                    <li>• Staff training on data protection</li>
                    <li>• Limited access to personal data</li>
                    <li>• Regular security assessments</li>
                    <li>• Incident response procedures</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Your Rights */}
        <Card className="shadow-lg">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <UserCheck className="w-6 h-6 text-purple-600" />
              <h2 className="text-2xl font-semibold text-gray-800">Your Rights</h2>
            </div>
          </CardHeader>
          <Divider />
          <CardBody className="pt-4">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-3">Data Rights</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2"></span>
                    <span className="text-gray-600">Access your personal data</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2"></span>
                    <span className="text-gray-600">Correct inaccurate information</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2"></span>
                    <span className="text-gray-600">Delete your data</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2"></span>
                    <span className="text-gray-600">Object to data processing</span>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-3">Account Control</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2"></span>
                    <span className="text-gray-600">Update profile information</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2"></span>
                    <span className="text-gray-600">Manage communication preferences</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2"></span>
                    <span className="text-gray-600">Download your data</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2"></span>
                    <span className="text-gray-600">Close your account</span>
                  </li>
                </ul>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Cookies and Tracking */}
        <Card className="shadow-lg">
          <CardHeader className="pb-3">
            <h2 className="text-2xl font-semibold text-gray-800">Cookies and Tracking</h2>
          </CardHeader>
          <Divider />
          <CardBody className="pt-4">
            <p className="text-gray-600 mb-4">
              We use cookies and similar technologies to enhance your experience, analyze usage, and provide personalized content.
            </p>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
              <p className="text-yellow-800">
                <strong>Cookie Notice:</strong> By continuing to use our website, you consent to our use of cookies. 
                You can manage your cookie preferences in your browser settings.
              </p>
            </div>
          </CardBody>
        </Card>

        {/* Third-Party Services */}
        <Card className="shadow-lg">
          <CardHeader className="pb-3">
            <h2 className="text-2xl font-semibold text-gray-800">Third-Party Services</h2>
          </CardHeader>
          <Divider />
          <CardBody className="pt-4">
            <p className="text-gray-600 mb-4">
              We may use third-party services to enhance our platform functionality:
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold text-gray-700">Analytics</h4>
                <p className="text-sm text-gray-600 mt-1">Usage tracking and analysis</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold text-gray-700">Payment Processing</h4>
                <p className="text-sm text-gray-600 mt-1">Secure payment handling</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold text-gray-700">Communication</h4>
                <p className="text-sm text-gray-600 mt-1">Email and messaging services</p>
              </div>
            </div>
          </CardBody>
        </Card>
        {/* Updates to Policy */}
        <Card className="shadow-lg">
          <CardBody>
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <h3 className="font-semibold text-blue-800 mb-2">Policy Updates</h3>
              <p className="text-blue-700 text-sm">
                We may update this Privacy Policy from time to time. We will notify you of any material changes 
                by posting the new Privacy Policy on this page and updating the "Last updated" date.
              </p>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

export default Privacy;